import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type {
  CertDayCheck,
  CertDayWarning,
  CrewMember,
  CrewOption,
  DayUsage,
  JobClassificationOption,
  JobPerDiem,
  PersonShiftInput,
  SubmissionInput,
  TimeEntryInput,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { useReference } from '@/lib/useReference'
import { dayLabel, useCertAccess } from '@/lib/certs'
import {
  clearDraft,
  loadDraft,
  newDraft,
  recallCrew,
  rememberCrew,
  saveDraft,
  type Draft,
  type DraftLine,
  type DraftShift,
} from '@/lib/draft'

/**
 * Sort order of the Regular work type. Every crew day starts as regular hours,
 * so it is the default line for everyone selected.
 */
const REGULAR_SORT = 10

/** Sage's per diem cost code. The work type is found by this, not by label. */
const PER_DIEM_COST_CODE = '860000000'

/** Travel's cost code, and the rider earnings code the field records against. */
const TRAVEL_COST_CODE = '802000000'
const TRAVEL_RIDER_EARNINGS = '000011'

/**
 * One line of the certification warning, in the supervisor's words. Medical
 * types arrive without dates below Admin, so the sentence leaves them out.
 */
function certWarningText(w: CertDayWarning): string {
  const where = w.is_job_scoped ? 'required on this job' : `required (${w.scope_label.toLowerCase()})`
  switch (w.outcome) {
    case 'Missing':
      return `no ${w.type_name} on file${w.pending_verification ? ' (one is waiting for the office to check)' : ''} — ${where}`
    case 'Expired':
      return `${w.type_name} expired${w.expires_date ? ` ${dayLabel(w.expires_date)}` : ''} — ${where}`
    case 'Blocked':
      return `${w.type_name} doesn't count yet (a prerequisite or re-evaluation) — ${where}`
    case 'Expiring':
      return `${w.type_name} expires${w.expires_date ? ` ${dayLabel(w.expires_date)}` : ' soon'}`
    case 'InGracePeriod':
      return `new hire: needs ${w.type_name}${w.grace_until ? ` by ${dayLabel(w.grace_until)}` : ' soon'}`
  }
}

/**
 * What the field calls a work type.
 *
 * Comes from the database (WorkType.FieldLabel), not from a constant here.
 * "Labor" on a supervisor's phone is "Regular" on the labor register — both
 * correct, for different readers — and holding the field word in front-end
 * code made the mapping invisible to anyone not reading TypeScript, and
 * certain to go stale the first time somebody added a work type.
 *
 * Blank means there is no separate field word, so the accounting label stands.
 */
function fieldLabel(w: { field_label?: string | null; label: string }): string {
  return w.field_label?.trim() || w.label
}

/**
 * Crew role, as a badge and a card stripe.
 *
 * The wording matches the per diem sheet exactly — Super, QC, Technician — so
 * nobody has to translate between the screen and the paper they already keep.
 */
const ROLE_STYLE: Record<string, { cls: string; card: string }> = {
  Super: { cls: 'role-super', card: 'is-super' },
  QC: { cls: 'role-qc', card: 'is-qc' },
  Technician: { cls: 'role-tech', card: 'is-tech' },
}

/**
 * Elapsed time between two "HH:MM" values, for display only.
 *
 * Returns null when either end is missing. An end earlier than the start means
 * the shift ran past midnight, so it wraps rather than going negative.
 *
 * This number is NEVER written to hours. On the crew sheet this screen mirrors,
 * a 5:30–4:00 span sits beside 10.00 paid hours, and 11.00 for one person in
 * the same window. Deriving one from the other would overwrite a real number
 * with a guess.
 */
function spanLabel(start: string, end: string): string | null {
  if (!start || !end) return null
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  if ([sh, sm, eh, em].some(Number.isNaN)) return null
  let minutes = eh * 60 + em - (sh * 60 + sm)
  if (minutes < 0) minutes += 24 * 60
  return `${(minutes / 60).toFixed(2)} hr span`
}

/**
 * Crew day entry.
 *
 * DESIGNED AROUND THE MEASURED DATA, not around what a time sheet looks like:
 *
 *   - Average crew is 6.1 and the maximum seen is 43, so the whole crew is
 *     entered in one pass. Per-person screens would be unusable.
 *   - Average is 1.44 lines per employee-day and 96% of employee-days are a
 *     single job, so the fast path is "everyone, Regular, same hours" and
 *     extra lines are the exception.
 *   - Crews are stable, so last time's crew for a job is recalled with one tap.
 *     Picking 6 names out of 138 is the slowest step, and this removes it.
 *
 * Target is under 60 seconds for a full crew day.
 */
export function NewSubmission() {
  const ref = useReference()
  const navigate = useNavigate()
  const { user } = useAuth()

  // Supervisors record their own crew only; office and admin cover anyone.
  // The API enforces this — here it only shapes what is offered.
  const canPickAnyone = user?.role === 'Office' || user?.role === 'Admin'

  const [draft, setDraft] = useState<Draft>(() => loadDraft() ?? newDraft())

  /**
   * Which step, held in the DRAFT rather than in useState.
   *
   * A pull-to-refresh mid-entry used to restore every hour and still land the
   * supervisor on step 1 with the crew picker reset — indistinguishable, from
   * the job site, from having lost the lot. Keeping the position beside the
   * data means a refresh returns them exactly where they were.
   */
  const step: 1 | 2 = draft.step ?? 1
  const setStep = useCallback(
    (next: 1 | 2) => setDraft((d) => ({ ...d, step: next })),
    [],
  )

  /**
   * Start each step at the top.
   *
   * Both steps are one long page in one component, so moving between them
   * changes nothing the browser considers a navigation and the scroll position
   * carries over. Picking a crew means scrolling to the bottom of step 1, so
   * Next used to land a supervisor a third of the way down step 2 -- past the
   * crew-wide hours control, which is the first thing they want and the whole
   * reason it sits at the top.
   *
   * `instant` rather than smooth: this is a page change, not a gesture, and an
   * animated jump on a phone reads as the screen having moved on its own.
   */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [step])
  const [jobFilter, setJobFilter] = useState('')
  const [bulkValue, setBulkValue] = useState('')
  const [bulkWorkTypeId, setBulkWorkTypeId] = useState<number>(0)
  const [crewOptions, setCrewOptions] = useState<CrewOption[] | null>(null)
  // Also in the draft, and for the same reason: resetting this on refresh
  // replaced the chosen crew with all 138 employees, which reads as "my crew
  // selection is gone" even though every tick is still set underneath.
  const pickedCrewId = draft.pickedCrewId ?? null
  const setPickedCrewId = useCallback(
    (next: number | null) => setDraft((d) => ({ ...d, pickedCrewId: next })),
    [],
  )
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Which per-person shift panels are expanded. Deliberately NOT in the draft:
  // it is view state, and restoring a draft should restore hours, not which
  // accordions happened to be open. Panels with real overrides re-open on
  // their own because the data says so.
  const [openShifts, setOpenShifts] = useState<Set<string>>(new Set())

  const openShift = useCallback((key: string) => {
    setOpenShifts((prev) => new Set(prev).add(key))
  }, [])

  // Per diem rates for the chosen job. Fetched for the whole field roster at
  // once so ticking a box is instant rather than a round trip per person.
  const [perDiem, setPerDiem] = useState<JobPerDiem | null>(null)

  useEffect(() => {
    if (!draft.jobNo) return setPerDiem(null)
    let cancelled = false
    void (async () => {
      try {
        const pd = await api.jobPerDiem(draft.jobNo)
        if (!cancelled) setPerDiem(pd)
      } catch {
        // A missing rate must not block time entry. The tick simply is not
        // offered, and hours still go in.
        if (!cancelled) setPerDiem(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [draft.jobNo])

  // Mirror every change so a screen lock or a phone call cannot lose a
  // half-entered crew day.
  useEffect(() => {
    saveDraft(draft)
  }, [draft])

  const update = useCallback((patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }))
  }, [])

  // No cost code step. Labor lands on the 800-series cost codes and the work
  // type carries the right one — asking a supervisor to also pick from the
  // job's budgeted codes was asking the same question twice.
  const regular =
    ref.workTypes.find((w) => w.sort_order === REGULAR_SORT) ?? ref.workTypes[0]

  // Default the crew-wide control to Regular: every crew day starts there.
  useEffect(() => {
    if (bulkWorkTypeId === 0 && regular) setBulkWorkTypeId(regular.work_type_id)
  }, [regular, bulkWorkTypeId])

  /**
   * Classifications this JOB can pay, on this date.
   *
   * On prevailing wage work the rate follows the work performed, not the job
   * title: a laborer who runs an excavator is owed the operator rate for those
   * hours. So a line can carry a classification the person does not normally
   * hold — but only one the job actually has a rate for.
   *
   * A job with a single rate returns one row and no control appears, which is
   * correct: there is nothing to split into. That is most jobs today.
   */
  const [jobClasses, setJobClasses] = useState<JobClassificationOption[]>([])

  useEffect(() => {
    if (!draft.jobNo || !draft.dateWorked) {
      setJobClasses([])
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const cls = await api.jobClassifications(draft.jobNo, draft.dateWorked)
        if (!cancelled) setJobClasses(cls)
      } catch {
        // Not fatal — entry carries on with everyone at their own
        // classification, which is what happened before this existed.
        if (!cancelled) setJobClasses([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [draft.jobNo, draft.dateWorked])

  /** Only worth offering when the job can pay more than one thing. */
  const canSplitTrade = jobClasses.length > 1

  /**
   * Travel, identified by its Job Cost code rather than its label or its id --
   * using the constant this file already keeps for it, which mirrors
   * TRAVEL_COST_CODE in the API.
   */
  const isTravelType = (workTypeId: number) =>
    ref.workTypes.find((w) => w.work_type_id === workTypeId)?.cost_code ===
    TRAVEL_COST_CODE

  const travelLines = draft.lines.filter((l) => isTravelType(l.workTypeId))

  /**
   * Jobs this supervisor is assigned to, floated to the top of the picker.
   *
   * ORDERING ONLY. A crew sent somewhere unexpected still has to be able to
   * record the drive, so every open job stays reachable underneath and the
   * server validates against all of them. Failure is silent by design: an
   * empty list costs the shortcut, not the feature.
   */
  /* --- On site -------------------------------------------------------------
   *
   * An explicit tap, not a silent capture. Silent would give better coverage
   * and would be worse on every other axis: it is weaker evidence because
   * nobody chose to assert anything, and it takes somebody's location without
   * them agreeing to it at that moment.
   *
   * The reading goes into the DRAFT, not straight to the server. A job site
   * with no signal is the normal case, and captured_utc -- the device clock at
   * the moment of the tap -- is what carries the meaning. The server records
   * when it eventually arrived separately, so a reading taken at 07:10 and
   * synced at 19:40 stays honest about both.
   *
   * It never blocks. A crew inside a steel tank gets no fix at all and the day
   * still has to be recordable.
   */
  const [locating, setLocating] = useState(false)
  const [locError, setLocError] = useState<string | null>(null)

  function captureLocation() {
    if (!draft.jobNo) {
      return setLocError('Pick a job first.')
    }
    if (!navigator.geolocation) {
      return setLocError('This device will not give a location.')
    }
    setLocating(true)
    setLocError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        update({
          checkIn: {
            // The job it was taken FOR. Without this, tapping on job A and
            // then switching to job B would post A's coordinates against B --
            // a reading that looks like evidence and is simply wrong.
            jobNo: draft.jobNo,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            // Metres, and worth keeping: a 2 km fix is a cell tower and
            // places nobody, but it looks like a good one once it is a pair
            // of numbers.
            accuracyMeters: Number.isFinite(pos.coords.accuracy)
              ? pos.coords.accuracy
              : null,
            capturedUtc: new Date().toISOString(),
          },
        })
        setLocating(false)
      },
      (err) => {
        setLocating(false)
        setLocError(
          err.code === err.PERMISSION_DENIED
            ? 'Location is turned off for this site. The day still records without it.'
            : 'Could not get a location just now. The day still records without it.',
        )
      },
      // High accuracy costs battery and a few seconds; this is one reading a
      // day, and a vague one is worth little.
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    )
  }

  const [myJobNos, setMyJobNos] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const mine = await api.myJobs()
        if (!cancelled) setMyJobNos(mine)
      } catch {
        if (!cancelled) setMyJobNos([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * The destination picker's options: assigned jobs first, then everything
   * else open. The crew-day's own job is excluded — "travelled to a different
   * project" is the whole premise, and offering it invites a no-op.
   */
  const destinationGroups = useMemo(() => {
    const others = ref.jobs.filter((j) => j.job_no !== draft.jobNo)
    const mine = new Set(myJobNos)
    return {
      assigned: others.filter((j) => mine.has(j.job_no)),
      rest: others.filter((j) => !mine.has(j.job_no)),
    }
  }, [ref.jobs, myJobNos, draft.jobNo])

  /** Point every travel line at one destination, or back at the crew-day job. */
  function setCrewDestination(jobNo: string | null) {
    update({
      lines: draft.lines.map((l) =>
        isTravelType(l.workTypeId) ? { ...l, overrideJobNo: jobNo } : l,
      ),
    })
  }

  /** Duplicate a line so the day can be split between two classifications.
   *  The new line starts with no hours: whoever splits it has to say how many,
   *  rather than the screen guessing a division of somebody's pay. */
  function splitForTrade(line: DraftLine, classificationId: number) {
    update({
      lines: [
        ...draft.lines,
        {
          ...line,
          id: crypto.randomUUID(),
          hours: '',
          amount: '',
          wageClassificationId: classificationId,
        },
      ],
    })
  }

  // Named crews to start from. A supervisor covering someone else's crew, or
  // an office user, would otherwise scroll the whole 138-strong field roster.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const opts = await api.crewOptions()
        if (!cancelled) setCrewOptions(opts)
      } catch {
        if (!cancelled) setCrewOptions([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * People on NOBODY's crew, and whoever already has hours on this date.
   *
   * 63 of 139 active field staff were on no crew when this was built, and none
   * of them could have a day recorded by anyone: the picker showed a
   * supervisor their own crew and the API refused the rest. Technicians only --
   * supervisors carry no self-assignment, so every one of them looks
   * unassigned, and offering them here would let any supervisor record any
   * other's hours.
   */
  const [floaters, setFloaters] = useState<CrewMember[]>([])
  const [showFloaters, setShowFloaters] = useState(false)
  const [floaterFilter, setFloaterFilter] = useState('')

  // Adding somebody Sage has never heard of. A new hire mobs straight to the
  // field and works before the office has entered them, so their hours have
  // nowhere to go. This gives them somewhere.
  const [newFirst, setNewFirst] = useState('')
  const [newLast, setNewLast] = useState('')
  const [newNote, setNewNote] = useState('')
  const [addingPerson, setAddingPerson] = useState(false)

  async function addNewPerson() {
    if (!newFirst.trim() || !newLast.trim()) {
      return setError('A first and last name are needed.')
    }
    setAddingPerson(true)
    setError(null)
    try {
      const person = await api.addProvisionalPerson({
        first_name: newFirst.trim(),
        last_name: newLast.trim(),
        note: newNote.trim() || null,
      })
      // Straight onto the day: adding somebody you are looking at and then
      // having to find them in a list is a step with no purpose.
      setFloaters((prev) => [...prev, person])
      update({ crewKeys: [...draft.crewKeys, person.sage_employee_key] })
      setNewFirst('')
      setNewLast('')
      setNewNote('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that person.')
    } finally {
      setAddingPerson(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const f = await api.floaters()
        if (!cancelled) setFloaters(f)
      } catch {
        // Not fatal: the supervisor's own crew still works exactly as before.
        if (!cancelled) setFloaters([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Hours somebody already has on this date, recorded by someone else.
   *
   * The crew boundary used to make double entry impossible as a side effect --
   * a person sat on one crew, so one supervisor could record them. Picking up
   * floaters removes that, so the collision has to be shown instead. A WARNING:
   * a morning on one job and an afternoon on another is a real day.
   */
  const [dayUsage, setDayUsage] = useState<DayUsage[]>([])

  useEffect(() => {
    if (!draft.dateWorked || draft.crewKeys.length === 0) {
      setDayUsage([])
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const u = await api.dayUsage(draft.dateWorked, draft.crewKeys)
        if (!cancelled) setDayUsage(u)
      } catch {
        if (!cancelled) setDayUsage([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [draft.dateWorked, draft.crewKeys])

  /**
   * Certifications this job requires of the people on it (Phase C).
   *
   * A WARNING, never a refusal -- the same footing as the day-usage check
   * above. Only asked when the certifications module is on for this
   * supervisor; for everyone else (Tim's testing included) this does nothing
   * and the screen is exactly as it was. Any failure reads as "nothing to say".
   */
  const certs = useCertAccess()
  const [certDay, setCertDay] = useState<CertDayCheck | null>(null)

  useEffect(() => {
    if (!certs.enabled || !certs.can('day_check') || !draft.jobNo || !draft.dateWorked
        || draft.crewKeys.length === 0) {
      setCertDay(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const r = await api.certDayCheck(draft.jobNo, draft.dateWorked, draft.crewKeys)
        if (!cancelled) setCertDay(r)
      } catch {
        if (!cancelled) setCertDay(null)
      }
    })()
    return () => {
      cancelled = true
    }
    // certs.can is a new function each render; enabled + loaded capture it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certs.enabled, certs.loaded, draft.jobNo, draft.dateWorked, draft.crewKeys])

  /**
   * Floaters who are ticked onto this day.
   *
   * Derived rather than stored: draft.crewKeys is the single source of who is
   * on the day, and anything that kept a parallel list would drift the moment
   * somebody was unticked from the other card.
   */
  const addedFloaters = useMemo(
    () =>
      floaters.filter((f) => draft.crewKeys.includes(f.sage_employee_key)),
    [floaters, draft.crewKeys],
  )

  /** Hours already on this date for one person, from a day that is not this one. */
  const usageFor = (key: string) =>
    dayUsage.filter(
      (u) => u.sage_employee_key === key && u.job_no !== draft.jobNo,
    )

  /** The pool to pick from: a chosen crew, or the full roster. */
  const pickedCrew = crewOptions?.find(
    (c) => c.supervisor_employee_id === pickedCrewId,
  )
  const memberPool = pickedCrew ? pickedCrew.members : ref.crew

  /**
   * Every person the day might mention, by key.
   *
   * FLOATERS BELONG IN HERE and it is not obvious why: they are picked from
   * their own card, but once ticked they are indistinguishable from crew
   * everywhere downstream. Step 2 renders each person through this map, and
   * so does the default labor code on a new line. Leaving them out gave a
   * floater no name on the hours screen and a null labor code on a certified
   * job -- neither of which a typecheck can see, because a missing Map entry
   * is just undefined.
   */
  const crewByKey = useMemo(
    () =>
      new Map(
        [...ref.crew, ...(pickedCrew?.members ?? []), ...floaters].map((c) => [
          c.sage_employee_key,
          c,
        ]),
      ),
    [ref.crew, pickedCrew, floaters],
  )

  function chooseCrew(crew: CrewOption | null) {
    setPickedCrewId(crew?.supervisor_employee_id ?? null)
    // The whole crew working the day is the normal case, so select them all.
    // Unticking one or two is faster than ticking six.
    update({ crewKeys: crew ? crew.members.map((m) => m.sage_employee_key) : [] })
  }

  const filteredJobs = useMemo(() => {
    const q = jobFilter.trim().toLowerCase()
    if (!q) return ref.jobs.slice(0, 40)
    return ref.jobs
      .filter(
        (j) =>
          j.job_no.toLowerCase().includes(q) ||
          (j.job_description ?? '').toLowerCase().includes(q),
      )
      .slice(0, 40)
  }, [ref.jobs, jobFilter])

  const selectedJob = ref.jobs.find((j) => j.job_no === draft.jobNo)
  const remembered = draft.jobNo ? recallCrew(draft.jobNo) : []

  function toggleCrew(key: string) {
    const has = draft.crewKeys.includes(key)
    update({
      crewKeys: has
        ? draft.crewKeys.filter((k) => k !== key)
        : [...draft.crewKeys, key],
    })
  }

  /** Turn the selected crew into one Regular line each, ready for hours. */
  function goToHours() {
    setError(null)
    if (!draft.jobNo) return setError('Pick a job first.')
    if (draft.crewKeys.length === 0) return setError('Pick at least one crew member.')
    if (!regular) return setError('No work types are set up. Ask the office.')

    const existing = new Map(
      draft.lines.map((l) => [`${l.sageEmployeeKey}|${l.workTypeId}`, l]),
    )
    const lines: DraftLine[] = draft.crewKeys.map((key) => {
      const found = existing.get(`${key}|${regular.work_type_id}`)
      if (found) return found
      return {
        id: crypto.randomUUID(),
        sageEmployeeKey: key,
        wageClassificationId: null,
        workTypeId: regular.work_type_id,
        entryType: regular.entry_type,
        // Classification defaults from the employee record. 31% of open jobs
        // are certified, so this must be present on every line — but it is
        // almost never a decision the supervisor has to make.
        laborCode: crewByKey.get(key)?.default_labor_code ?? null,
        hours: '',
        amount: '',
      }
    })
    // Keep any extra lines already added for still-selected crew.
    const extras = draft.lines.filter(
      (l) =>
        l.workTypeId !== regular.work_type_id &&
        draft.crewKeys.includes(l.sageEmployeeKey),
    )
    update({ lines: [...lines, ...extras] })
    setStep(2)
  }

  /**
   * Apply one work type to the whole crew at once.
   *
   * Travel and per diem are crew-wide facts far more often than individual
   * ones — the whole crew rode out together, the whole crew got per diem.
   * Adding those person by person is the slowest thing in the screen.
   *
   * Updates the value where the line already exists and creates it where it
   * does not, so pressing Apply twice is harmless.
   */
  function applyToCrew() {
    const wt = ref.workTypes.find((w) => w.work_type_id === bulkWorkTypeId)
    const v = bulkValue.trim()
    if (!wt || !v) return

    const isHours = wt.entry_type === 'Hours'
    const next = [...draft.lines]

    for (const key of draft.crewKeys) {
      const existing = next.findIndex(
        (l) => l.sageEmployeeKey === key && l.workTypeId === wt.work_type_id,
      )
      if (existing >= 0) {
        next[existing] = {
          ...next[existing],
          hours: isHours ? v : '',
          amount: isHours ? '' : v,
        }
      } else {
        next.push({
          id: crypto.randomUUID(),
          sageEmployeeKey: key,
          // Ordinary line: the person's own classification.
          wageClassificationId: null,
          workTypeId: wt.work_type_id,
          entryType: wt.entry_type,
          laborCode: crewByKey.get(key)?.default_labor_code ?? null,
          hours: isHours ? v : '',
          amount: isHours ? '' : v,
        })
      }
    }
    update({ lines: next })
    setBulkValue('')
  }

  /** Undo a crew-wide apply without removing six lines by hand. */
  function removeFromCrew() {
    if (!regular || bulkWorkTypeId === regular.work_type_id) return
    update({
      lines: draft.lines.filter((l) => l.workTypeId !== bulkWorkTypeId),
    })
  }

  function setLine(id: string, patch: Partial<DraftLine>) {
    update({ lines: draft.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)) })
  }

  function addLine(key: string) {
    // Default the extra line to the first work type that is not Regular —
    // an extra line is by definition something other than the standard day.
    const wt = selectableTypes.find((w) => w.work_type_id !== regular?.work_type_id)
    if (!wt) return
    update({
      lines: [
        ...draft.lines,
        {
          id: crypto.randomUUID(),
          sageEmployeeKey: key,
          // Ordinary line: the person's own classification.
          wageClassificationId: null,
          workTypeId: wt.work_type_id,
          entryType: wt.entry_type,
          laborCode: crewByKey.get(key)?.default_labor_code ?? null,
          hours: '',
          amount: '',
        },
      ],
    })
  }

  /* Per diem ---------------------------------------------------------------
     A tick, not a typed figure. The amount depends on the job's location and
     the person's tier, neither of which the supervisor picks, so there is
     nothing here for them to decide — only to confirm.
  ------------------------------------------------------------------------- */

  // Identified by cost code rather than by label, so renaming the work type
  // on the admin screen cannot quietly break this.
  const perDiemType = ref.workTypes.find((w) => w.cost_code === PER_DIEM_COST_CODE)

  // The single "Travel" option the field records against. Whether a given
  // person's travel pays the rider or the driver code is settled server-side
  // from the tick below — the driver type is not offered here at all.
  const travelType = ref.workTypes.find(
    (w) =>
      w.cost_code === TRAVEL_COST_CODE &&
      w.earnings_code === TRAVEL_RIDER_EARNINGS,
  )

  const isDriver = (key: string) => draft.drivers.includes(key)

  function toggleDriver(key: string) {
    update({
      drivers: isDriver(key)
        ? draft.drivers.filter((k) => k !== key)
        : [...draft.drivers, key],
    })
  }

  /** Work types a line may use — everything except per diem. */
  const selectableTypes = ref.workTypes.filter(
    (w) => w.cost_code !== PER_DIEM_COST_CODE,
  )

  const perDiemFor = useCallback(
    (key: string) =>
      perDiem?.people.find((p) => p.sage_employee_key === key)?.daily_amount ?? null,
    [perDiem],
  )

  const perDiemEligible = draft.crewKeys.filter((k) => perDiemFor(k) !== null).length
  const perDiemCount = perDiemType
    ? draft.lines.filter((l) => l.workTypeId === perDiemType.work_type_id).length
    : 0
  const perDiemTotal = perDiemType
    ? draft.lines
        .filter((l) => l.workTypeId === perDiemType.work_type_id)
        .reduce((sum, l) => sum + Number(l.amount || 0), 0)
    : 0

  function perDiemLine(key: string) {
    return draft.lines.find(
      (l) => l.sageEmployeeKey === key && l.workTypeId === perDiemType?.work_type_id,
    )
  }

  function togglePerDiem(key: string) {
    const existing = perDiemLine(key)
    if (existing) return removeLine(existing.id)

    const amount = perDiemFor(key)
    if (!perDiemType || amount === null) return
    update({
      lines: [
        ...draft.lines,
        {
          id: crypto.randomUUID(),
          sageEmployeeKey: key,
          // Ordinary line: the person's own classification.
          wageClassificationId: null,
          workTypeId: perDiemType.work_type_id,
          entryType: 'Amount',
          laborCode: crewByKey.get(key)?.default_labor_code ?? null,
          hours: '',
          amount: String(amount),
        },
      ],
    })
  }

  /** Tick or untick the whole crew — the usual case on a travelling job. */
  function togglePerDiemAll(on: boolean) {
    if (!perDiemType) return
    const others = draft.lines.filter((l) => l.workTypeId !== perDiemType.work_type_id)
    if (!on) return update({ lines: others })
    update({
      lines: [
        ...others,
        ...draft.crewKeys.flatMap((key) => {
          const amount = perDiemFor(key)
          // Anyone with no rate — an office user on the crew — is skipped
          // rather than given a zero, which would post a $0.00 line.
          if (amount === null) return []
          return [{
            id: crypto.randomUUID(),
            sageEmployeeKey: key,
          // Ordinary line: the person's own classification.
          wageClassificationId: null,
            workTypeId: perDiemType.work_type_id,
            entryType: 'Amount' as const,
            laborCode: crewByKey.get(key)?.default_labor_code ?? null,
            hours: '',
            amount: String(amount),
          }]
        }),
      ],
    })
  }

  function removeLine(id: string) {
    update({ lines: draft.lines.filter((l) => l.id !== id) })
  }

  /**
   * Take someone off the day entirely.
   *
   * Someone selected by "same crew as last time" who did not actually turn up
   * is the common case, and unticking them back on the previous screen loses
   * the hours already typed for everyone else.
   */
  function removePerson(key: string) {
    // Their shift override goes too. Leaving it behind would silently
    // reattach if the same person were added back later in the same draft.
    const { [key]: _dropped, ...shifts } = draft.shifts
    update({
      crewKeys: draft.crewKeys.filter((k) => k !== key),
      lines: draft.lines.filter((l) => l.sageEmployeeKey !== key),
      shifts,
    })
  }

  /** Set part of one person's shift override, creating it if needed. */
  function setShift(key: string, patch: Partial<DraftShift>) {
    const current = draft.shifts[key] ?? { start: '', end: '', note: '' }
    update({ shifts: { ...draft.shifts, [key]: { ...current, ...patch } } })
  }

  /** Drop the override so this person follows the crew again. */
  function clearShift(key: string) {
    const { [key]: _dropped, ...shifts } = draft.shifts
    update({ shifts })
    setOpenShifts((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }

  /** Changing work type flips the input between hours and a dollar amount. */
  function changeWorkType(id: string, workTypeId: number) {
    const wt = ref.workTypes.find((w) => w.work_type_id === workTypeId)
    if (!wt) return
    setLine(id, { workTypeId, entryType: wt.entry_type, hours: '', amount: '' })
  }

  const totals = useMemo(() => {
    let hours = 0
    let amount = 0
    for (const l of draft.lines) {
      if (l.entryType === 'Hours') hours += Number(l.hours) || 0
      else amount += Number(l.amount) || 0
    }
    return { hours, amount }
  }, [draft.lines])

  async function submit() {
    setError(null)

    const entries: TimeEntryInput[] = []
    for (const l of draft.lines) {
      const isHours = l.entryType === 'Hours'
      const value = Number(isHours ? l.hours : l.amount)
      if (!value) continue // blank rows are simply not submitted
      if (isHours && (value <= 0 || value > 24)) {
        return setError(`Hours must be between 0 and 24 (${label(l)}).`)
      }
      entries.push({
        time_entry_id: l.id,
        sage_employee_key: l.sageEmployeeKey,
        // The server resolves cost code and earnings code from this, so a
        // mismatched pairing cannot be produced by the client at all.
        work_type_id: l.workTypeId,
        labor_code: l.laborCode,
        hours_worked: isHours ? value : null,
        amount: isHours ? null : value,
        // Null on every ordinary line. Set only where somebody worked out of
        // trade, and the server refuses it if the job has no such rate.
        wage_classification_id: l.wageClassificationId,
        // Null on every ordinary line. Set only on travel booked to the job
        // they drove TO; the server refuses it on anything but travel, and
        // normalises the crew-day's own job back to null.
        override_job_no: l.overrideJobNo ?? null,
      })
    }

    if (entries.length === 0) {
      return setError('Nothing to submit — enter hours or an amount for at least one person.')
    }

    // A READING OR A REASON. Not a wall: any reason at all gets the day
    // through, so a crew with no signal can always record its hours. The
    // office records days from paper and is not asked.
    const hasReading = !!draft.checkIn && draft.checkIn.jobNo === draft.jobNo
    const noLocReason = (draft.noCheckInReason ?? '').trim()
    if (!canPickAnyone && !hasReading && !noLocReason) {
      return setError(
        'No location recorded. Say why in the box above the Submit button ' +
          '(e.g. "at the hotel") — or go back and tap "I\'m on site".',
      )
    }

    // Shifts and narratives are optional throughout. A person is sent only if
    // something was actually recorded for them — either the crew's shift, or
    // an override of their own. Sending a row of nulls for everyone would
    // turn "not recorded" into "recorded as nothing".
    const people: PersonShiftInput[] = []
    for (const key of draft.crewKeys) {
      const o = draft.shifts[key]
      const start = o?.start || draft.crewStart
      const end = o?.end || draft.crewEnd
      const note = (o?.note ?? '').trim()
      const driving = draft.drivers.includes(key)
      // A ticked driver counts as something recorded even with no shift and no
      // note — it is what pays them the driver rate, so skipping them here
      // would silently demote them to rider.
      if (!start && !end && !note && !driving) continue
      people.push({
        sage_employee_key: key,
        start_time: start || null,
        end_time: end || null,
        // Only the override. The crew note lives on the submission, so it is
        // stored once rather than copied onto everybody.
        work_performed: note || null,
        is_driver: driving,
      })
    }

    const payload: SubmissionInput = {
      submission_id: draft.submissionId,
      job_no: draft.jobNo,
      date_worked: draft.dateWorked,
      note: draft.note || null,
      work_performed: draft.workPerformed.trim() || null,
      client_captured_utc: draft.startedUtc,
      entries,
      people,
      no_check_in_reason: hasReading ? null : noLocReason || null,
    }

    setSubmitting(true)
    try {
      const res = await api.submit(payload)
      rememberCrew(draft.jobNo, draft.crewKeys)

      // Sent AFTER the day is accepted, so a refused check-in can never cost
      // somebody their hours. But AWAITED, and its outcome carried into the
      // message -- the first version fired this off and ignored the result, so
      // a check-in the server refused looked exactly like one that worked. The
      // screen said "Location recorded" and the database had nothing in it.
      //
      // A failure here is a footnote on a successful day, not an error: the
      // hours are safe either way, and the wording says so.
      let locNote = ''
      if (draft.checkIn && draft.checkIn.jobNo === draft.jobNo) {
        try {
          await api.checkIn({
            // No key: the server attributes it to the caller's own portal
            // identity, which exists for everybody. One tap, one row, theirs.
            job_no: draft.jobNo,
            date_worked: draft.dateWorked,
            latitude: draft.checkIn.latitude,
            longitude: draft.checkIn.longitude,
            accuracy_meters: draft.checkIn.accuracyMeters,
            captured_utc: draft.checkIn.capturedUtc,
          })
          locNote = ' Location saved.'
        } catch (err) {
          locNote =
            ' Location NOT saved: ' +
            (err instanceof ApiError ? err.message : 'unknown error')
        }
      }

      clearDraft()
      navigate('/field', {
        state: {
          flash:
            (res.already_submitted
              ? 'That crew day was already recorded.'
              : `Sent — ${res.entries_accepted} lines for ${draft.crewKeys.length} crew.`) +
            locNote,
        },
      })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send. It is saved on this phone — tap Submit crew day again once you have signal.')
    } finally {
      setSubmitting(false)
    }
  }

  function label(l: DraftLine): string {
    return crewByKey.get(l.sageEmployeeKey)?.display_name ?? l.sageEmployeeKey
  }

  // ------------------------------------------------------------------ render

  if (ref.loading && ref.jobs.length === 0) {
    return <div className="card">Loading jobs and crew…</div>
  }

  return (
    <div className="stack">
      {ref.stale && (
        <div className="alert alert-warn">
          Showing saved job and crew lists — could not reach the server. You can
          still record time; it is saved on this phone. Tap Submit crew day
          again once you have signal — it does not send by itself.
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}

      {step === 1 ? (
        <>
          <div className="card">
            <h2>Job</h2>
            {selectedJob ? (
              <div className="stack">
                <div>
                  <strong className="mono">{selectedJob.job_no}</strong>{' '}
                  {selectedJob.job_description}
                  {selectedJob.is_certified && (
                    <span className="badge badge-info" style={{ marginLeft: 8 }}>
                      Certified
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => update({ jobNo: '', checkIn: null })}
                >
                  Change job
                </button>
              </div>
            ) : (
              <>
                <div className="form-field">
                  <label htmlFor="jobFilter">Search</label>
                  <input
                    id="jobFilter"
                    value={jobFilter}
                    onChange={(e) => setJobFilter(e.target.value)}
                    placeholder="Job number or name"
                    autoCapitalize="characters"
                  />
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {filteredJobs.map((j) => (
                    <button
                      key={j.job_no}
                      className="btn btn-secondary btn-block"
                      style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                      onClick={() =>
                        update({
                          jobNo: j.job_no,
                          // A location taken at the previous job says nothing
                          // about this one. Dropped rather than carried, so
                          // the only way to have a reading for a job is to
                          // have stood on it.
                          checkIn:
                            draft.checkIn && draft.checkIn.jobNo !== j.job_no
                              ? null
                              : draft.checkIn,
                        })
                      }
                    >
                      <span className="mono">{j.job_no}</span>
                      <span className="muted" style={{ fontWeight: 400 }}>
                        {j.job_description}
                      </span>
                    </button>
                  ))}
                  {filteredJobs.length === 0 && <p className="muted">No matching jobs.</p>}
                </div>
              </>
            )}
          </div>

          <div className="card">
            <h2>Date</h2>
            <div className="form-field">
              <input
                type="date"
                value={draft.dateWorked}
                onChange={(e) => update({ dateWorked: e.target.value })}
              />
            </div>
          </div>

          {/* ON SITE.

              Offered once a job is chosen, because the reading is meaningless
              without one. Deliberately here on step 1 rather than at submit:
              step 1 is the morning fact -- which job, which crew -- and submit
              happens at a kitchen table at 7pm. A reading taken then records
              where the paperwork was done, which is not the question.

              The tap is worth making because it is honest about what it
              proves: that this person was here, not that the crew was. */}
          {draft.jobNo && (
            <div className="card">
              <h2>On site</h2>
              {draft.checkIn ? (
                <>
                  <div className="alert alert-ok" style={{ marginBottom: '0.5rem' }}>
                    Location recorded at{' '}
                    {new Date(draft.checkIn.capturedUtc).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {draft.checkIn.accuracyMeters != null && (
                      <> · accurate to about {Math.round(draft.checkIn.accuracyMeters)} m</>
                    )}
                  </div>
                  <button className="btn btn-secondary btn-block" onClick={captureLocation}>
                    Take it again
                  </button>
                </>
              ) : (
                <>
                  <p className="muted" style={{ marginTop: 0 }}>
                    Records that <strong>you</strong> were here, with the time you
                    tapped. Can’t do it? You’ll be asked why when you submit —
                    the day records either way.
                  </p>
                  <button
                    className="btn btn-block"
                    disabled={locating}
                    onClick={captureLocation}
                  >
                    {locating ? 'Getting location…' : "I'm on site"}
                  </button>
                </>
              )}
              {locError && (
                <div className="alert alert-warn" style={{ marginTop: '0.5rem' }}>
                  {locError}
                </div>
              )}
            </div>
          )}

          {crewOptions !== null && crewOptions.length > 0 && (
            <div className="card">
              <h2>{canPickAnyone ? 'Whose crew?' : 'Your crew'}</h2>
              <p className="muted">
                {canPickAnyone
                  ? 'Pick a crew to start from its members, or work from the full field roster.'
                  : 'Your crew, including yourself. To record time for someone on another crew, ask the office.'}
              </p>
              {crewOptions.map((c) => (
                <button
                  key={c.supervisor_employee_id}
                  className={`btn ${
                    pickedCrewId === c.supervisor_employee_id ? '' : 'btn-secondary'
                  } btn-block`}
                  style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                  aria-pressed={pickedCrewId === c.supervisor_employee_id}
                  onClick={() => chooseCrew(c)}
                >
                  <span style={{ width: '1.5rem' }}>
                    {pickedCrewId === c.supervisor_employee_id ? '✓' : ''}
                  </span>
                  {c.supervisor_name}
                  <span className="muted" style={{ fontWeight: 400, marginLeft: 'auto' }}>
                    {c.members.length}
                  </span>
                </button>
              ))}
              {/* Only offered to office and admin. A supervisor's roster is
                  already limited to their own crew server-side, so the option
                  would list the same people under a misleading label. */}
              {canPickAnyone && (
                <button
                  className={`btn ${pickedCrewId === null ? '' : 'btn-secondary'} btn-block`}
                  style={{ justifyContent: 'flex-start' }}
                  aria-pressed={pickedCrewId === null}
                  onClick={() => chooseCrew(null)}
                >
                  <span style={{ width: '1.5rem' }}>{pickedCrewId === null ? '✓' : ''}</span>
                  All field staff
                  <span className="muted" style={{ fontWeight: 400, marginLeft: 'auto' }}>
                    {ref.crew.length}
                  </span>
                </button>
              )}
            </div>
          )}

          <div className="card">
            <h2>
              Crew ({draft.crewKeys.length}
              {pickedCrew ? ` of ${pickedCrew.members.length}` : ''})
            </h2>

            {remembered.length > 0 && draft.crewKeys.length === 0 && (
              <button
                className="btn btn-secondary btn-block"
                style={{ marginBottom: 12 }}
                onClick={() => update({ crewKeys: remembered })}
              >
                Same crew as last time ({remembered.length})
              </button>
            )}

            {memberPool.length === 0 ? (
              <p className="muted">
                No crew available. Employee names have to be loaded into the
                portal before anyone can be selected.
              </p>
            ) : (
              memberPool.map((c) => {
                const on = draft.crewKeys.includes(c.sage_employee_key)
                return (
                  <button
                    key={c.sage_employee_key}
                    className={`btn ${on ? '' : 'btn-secondary'} btn-block`}
                    style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                    aria-pressed={on}
                    onClick={() => toggleCrew(c.sage_employee_key)}
                  >
                    <span style={{ width: '1.5rem' }}>{on ? '✓' : ''}</span>
                    {c.display_name}
                    <span className="mono muted" style={{ fontWeight: 400 }}>
                      {c.employee_no}
                    </span>
                    {c.is_supervisor && (
                      <span className="badge badge-info" style={{ marginLeft: 'auto' }}>
                        supervisor
                      </span>
                    )}
                  </button>
                )
              })
            )}

            {/* WARN, DO NOT REFUSE.

                Somebody here already has hours on this date from a different
                job. That is often a genuine split day -- a morning on one job,
                an afternoon on another -- so it is shown and left to the
                person who knows which it is. Before floaters could be picked
                up this was impossible by construction; now it is merely
                unlikely, which is a different thing. */}
            {/* TICKED FLOATERS BELONG IN THE CREW LIST.

                They are picked from their own card below, but once ticked they
                are on the day exactly like anyone else -- and collapsing that
                card used to hide them, so the crew count said 8 and the list
                showed 5. The count was right and the list was lying.

                Kept visually distinct rather than merged into the pool above:
                they are not on this supervisor's crew, and the day is a
                one-off, so the screen should not start implying otherwise. */}
            {addedFloaters.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <div
                  className="muted"
                  style={{
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: 4,
                  }}
                >
                  Added for today
                </div>
                {addedFloaters.map((c) => (
                  <button
                    key={c.sage_employee_key}
                    className="btn btn-block"
                    style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                    aria-pressed
                    onClick={() => toggleCrew(c.sage_employee_key)}
                  >
                    <span style={{ width: '1.5rem' }}>{'✓'}</span>
                    {c.display_name}
                    <span className="mono muted" style={{ fontWeight: 400 }}>
                      {c.employee_no}
                    </span>
                    <span
                      className="badge badge-info"
                      style={{ marginLeft: 'auto' }}
                    >
                      {/* P#### is the provisional numbering; anyone else here
                          is simply on no crew. Worth distinguishing, because
                          one of them cannot be paid until the office acts. */}
                      {/^P\d{4}$/.test(c.employee_no) ? 'new hire' : 'not your crew'}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {draft.crewKeys.some((k) => usageFor(k).length > 0) && (
              <div className="alert alert-warn" style={{ marginTop: '0.75rem' }}>
                Already recorded elsewhere on this date:
                <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
                  {draft.crewKeys.flatMap((k) =>
                    usageFor(k).map((u) => (
                      <li key={`${k}-${u.job_no}`}>
                        <strong>{u.display_name}</strong> — {Number(u.hours).toFixed(2)} h
                        on {u.job_no}
                        {u.submitted_by ? `, by ${u.submitted_by}` : ''}
                      </li>
                    )),
                  )}
                </ul>
                <div style={{ marginTop: '0.4rem' }}>
                  Fine if they split the day between jobs. Untick them if not.
                </div>
              </div>
            )}

            {certDay && certDay.people.length > 0 && (
              <div
                className={`alert ${
                  certDay.people.some((p) =>
                    p.warnings.some((w) => ['Missing', 'Expired', 'Blocked'].includes(w.outcome)),
                  )
                    ? 'alert-warn'
                    : 'alert-info'
                }`}
                style={{ marginTop: '0.75rem' }}
              >
                Certifications for this job:
                <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
                  {certDay.people.map((p) => (
                    <li key={p.sage_employee_key}>
                      <strong>{p.display_name}</strong> — {p.warnings.map(certWarningText).join('; ')}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: '0.4rem' }}>
                  You can still record them. The office sees this too.
                </div>
              </div>
            )}
          </div>

          {/* PEOPLE ON NOBODY'S CREW.

              Collapsed by default: the common case is a supervisor's own crew
              and this must not lengthen it. Kept a separate card rather than
              merged into the list above so that "who is mine" stays legible --
              47 extra names in the main list would bury the 5 that matter. */}
          {floaters.length > 0 && (
            <div className="card">
              {!showFloaters ? (
                <button
                  className="btn btn-secondary btn-block"
                  onClick={() => setShowFloaters(true)}
                >
                  + Add someone not on your crew ({floaters.length})
                </button>
              ) : (
                <>
                  <h2>Not on your crew</h2>
                  <p className="muted">
                    Anyone not assigned to a crew. Pick them up for today only —
                    this does not move them onto your crew.
                  </p>
                  <div className="form-field">
                    <input
                      type="search"
                      value={floaterFilter}
                      onChange={(e) => setFloaterFilter(e.target.value)}
                      placeholder="Search by name or number"
                      aria-label="Search people not on your crew"
                    />
                  </div>
                  {floaters
                    .filter((c) => {
                      const q = floaterFilter.trim().toLowerCase()
                      if (!q) return true
                      return (
                        c.display_name.toLowerCase().includes(q) ||
                        c.employee_no.toLowerCase().includes(q)
                      )
                    })
                    // Ticked people stay visible even when they no longer match
                    // the search, so nobody is silently dropped from the day.
                    .concat(
                      floaters.filter(
                        (c) =>
                          draft.crewKeys.includes(c.sage_employee_key) &&
                          floaterFilter.trim() !== '' &&
                          !c.display_name
                            .toLowerCase()
                            .includes(floaterFilter.trim().toLowerCase()),
                      ),
                    )
                    .slice(0, 40)
                    .map((c) => {
                      const on = draft.crewKeys.includes(c.sage_employee_key)
                      return (
                        <button
                          key={c.sage_employee_key}
                          className={`btn ${on ? '' : 'btn-secondary'} btn-block`}
                          style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                          aria-pressed={on}
                          onClick={() => toggleCrew(c.sage_employee_key)}
                        >
                          <span style={{ width: '1.5rem' }}>{on ? '✓' : ''}</span>
                          {c.display_name}
                          <span className="mono muted" style={{ fontWeight: 400 }}>
                            {c.employee_no}
                          </span>
                        </button>
                      )
                    })}
                  {/* NOT IN SAGE AT ALL.

                      A new hire does the packet with the supervisor on site
                      and there is turnaround before the office enters them. In
                      the meantime they are working. This records a placeholder
                      so the hours exist; the office matches it to the real
                      record later, and nothing exports until they do.

                      Deliberately below the search: almost everybody typing a
                      name here is looking for somebody who already exists, and
                      finding them is always better than creating a duplicate. */}
                  <details style={{ marginTop: '0.75rem' }}>
                    <summary className="muted">
                      Not on this list? Add someone new to the company
                    </summary>
                    <p className="muted" style={{ fontSize: '0.8rem' }}>
                      For a new hire the office has not entered yet. Their hours
                      are held until the office matches them up.
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <div className="form-field" style={{ flex: 1 }}>
                        <label htmlFor="newFirst">First name</label>
                        <input
                          id="newFirst"
                          value={newFirst}
                          onChange={(e) => setNewFirst(e.target.value)}
                          autoComplete="off"
                        />
                      </div>
                      <div className="form-field" style={{ flex: 1 }}>
                        <label htmlFor="newLast">Last name</label>
                        <input
                          id="newLast"
                          value={newLast}
                          onChange={(e) => setNewLast(e.target.value)}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label htmlFor="newNote">
                        Anything that will help the office identify them
                      </label>
                      <input
                        id="newNote"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Started Tuesday on N25215, Juan's brother"
                      />
                    </div>
                    <button
                      className="btn btn-block"
                      disabled={addingPerson || !newFirst.trim() || !newLast.trim()}
                      onClick={() => void addNewPerson()}
                    >
                      {addingPerson ? 'Adding…' : 'Add to today'}
                    </button>
                  </details>

                  <button
                    className="btn btn-secondary btn-block"
                    style={{ marginTop: 6 }}
                    onClick={() => setShowFloaters(false)}
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          )}

          <button className="btn btn-block" onClick={goToHours}>
            Next — enter hours
          </button>
        </>
      ) : (
        <>
          <div className="card">
            <h2 className="title">
              <span className="mono">{draft.jobNo}</span> · {draft.dateWorked}
            </h2>
            <button className="btn btn-secondary" onClick={() => setStep(1)}>
              Back to crew
            </button>
          </div>

          {(() => {
            const bulkType = ref.workTypes.find(
              (w) => w.work_type_id === bulkWorkTypeId,
            )
            const isHours = bulkType?.entry_type !== 'Amount'
            const isRegular = bulkWorkTypeId === regular?.work_type_id
            const alreadyApplied = draft.lines.some(
              (l) => l.workTypeId === bulkWorkTypeId,
            )
            return (
              <div className="card">
                <h2>Apply to the whole crew</h2>
                <p className="muted">
                  Set the same hours or amount for everyone at once. Travel and
                  per diem usually apply to the whole crew — adjust anyone who
                  differs below.
                </p>

                <div className="form-field">
                  <label htmlFor="bulkType">Type of work</label>
                  <select
                    id="bulkType"
                    value={bulkWorkTypeId}
                    onChange={(e) => {
                      setBulkWorkTypeId(Number(e.target.value))
                      setBulkValue('')
                    }}
                  >
                    {ref.workTypes.map((w) => (
                      <option key={w.work_type_id} value={w.work_type_id}>
                        {fieldLabel(w)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                  <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                    <label htmlFor="bulkValue">
                      {isHours ? 'Hours each' : 'Amount each'}
                    </label>
                    <input
                      id="bulkValue"
                      type="number"
                      inputMode="decimal"
                      step={isHours ? '0.25' : '0.01'}
                      min="0"
                      max={isHours ? '24' : undefined}
                      value={bulkValue}
                      onChange={(e) => setBulkValue(e.target.value)}
                      placeholder={isHours ? '8' : '75.00'}
                    />
                  </div>
                  <button className="btn" onClick={applyToCrew} disabled={!bulkValue.trim()}>
                    Apply to all {draft.crewKeys.length}
                  </button>
                </div>

                {/* Only offered for extra lines — every crew day has regular
                    hours, so removing them wholesale is never the intent. */}
                {!isRegular && alreadyApplied && (
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: '0.75rem' }}
                    onClick={removeFromCrew}
                  >
                    Remove {bulkType ? fieldLabel(bulkType) : ""} from everyone
                  </button>
                )}
              </div>
            )
          })()}

          {/* WHERE THE TRAVEL IS CHARGED.

              A crew works job X until early afternoon then drives to job Y for
              the next day. The drive is a cost of getting to Y, so that is
              where the office books it — with occasional exceptions, which is
              why this is a question and not a rule.

              Appears ONLY once travel hours exist, so an ordinary crew day
              never sees it. Ticking reveals the picker; unticking puts every
              travel line back on the crew-day's job rather than leaving a
              half-set destination behind. */}
          {travelLines.length > 0 && (
            <div className="card">
              <h2>Travel</h2>
              <label className="checkline">
                <input
                  type="checkbox"
                  checked={travelLines.some((l) => l.overrideJobNo)}
                  onChange={(e) =>
                    setCrewDestination(
                      e.target.checked
                        ? destinationGroups.assigned[0]?.job_no ??
                            destinationGroups.rest[0]?.job_no ??
                            null
                        : null,
                    )
                  }
                />
                Travelled to a different project
                <span className="muted" style={{ fontSize: '0.75rem' }}>
                  · charges the travel to the job they drove to
                </span>
              </label>

              {travelLines.some((l) => l.overrideJobNo) && (
                <div className="form-field" style={{ marginTop: '0.75rem' }}>
                  <label htmlFor="crewDestination">Travelled to</label>
                  <select
                    id="crewDestination"
                    value={travelLines.find((l) => l.overrideJobNo)?.overrideJobNo ?? ''}
                    onChange={(e) => setCrewDestination(e.target.value || null)}
                  >
                    {/* The supervisor's own jobs first: on a 325-job list the
                        destination is nearly always one of theirs, but never
                        guaranteed, so the rest stay reachable below. */}
                    {destinationGroups.assigned.length > 0 && (
                      <optgroup label="Your jobs">
                        {destinationGroups.assigned.map((j) => (
                          <option key={j.job_no} value={j.job_no}>
                            {j.job_no} · {j.job_description}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label="All open jobs">
                      {destinationGroups.rest.map((j) => (
                        <option key={j.job_no} value={j.job_no}>
                          {j.job_no} · {j.job_description}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="muted" style={{ fontSize: '0.8rem', marginTop: '0.4rem' }}>
                    Applies to everyone's travel. Change one person's below if
                    they went somewhere else.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* The crew's shift and what they did, written once. This is the
              header block of the paper Daily Time Sheet: one In and one Out
              for the whole crew, one description of the day's work. Both are
              optional — leave them blank and nothing is recorded. */}
          <div className="card">
            <h2>Shift and work performed (optional)</h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                <label htmlFor="crewStart">Start</label>
                <input
                  id="crewStart"
                  type="time"
                  value={draft.crewStart}
                  onChange={(e) => update({ crewStart: e.target.value })}
                />
              </div>
              <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                <label htmlFor="crewEnd">End</label>
                <input
                  id="crewEnd"
                  type="time"
                  value={draft.crewEnd}
                  onChange={(e) => update({ crewEnd: e.target.value })}
                />
              </div>
              {/* Shown because it is useful to see, greyed because it is not
                  the number that gets paid. Hours are entered above and are
                  deliberately not derived from this. */}
              <div style={{ flex: 1, paddingBottom: '0.6rem' }}>
                <span className="muted" style={{ fontSize: '0.8125rem' }}>
                  {spanLabel(draft.crewStart, draft.crewEnd) ?? '—'}
                </span>
              </div>
            </div>
            <div className="form-field" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
              <label htmlFor="crewWork">Work performed</label>
              <input
                id="crewWork"
                value={draft.workPerformed}
                onChange={(e) => update({ workPerformed: e.target.value })}
                placeholder="What the crew did today"
                maxLength={500}
              />
            </div>
            <p className="muted" style={{ fontSize: '0.8125rem', marginTop: '0.5rem' }}>
              Applies to everyone. Change it for one person below if their day
              was different.
            </p>
          </div>

          {/* Per diem is a tick, not a figure. What it is worth depends on
              where the job is and what the person does — the supervisor
              chooses neither, so there is nothing to type. */}
          {perDiemType && perDiem && (
            <div className="card">
              <h2>Per diem</h2>
              {perDiem.has_rate ? (
                <>
                  <p className="muted" style={{ fontSize: '0.8125rem', marginTop: 0 }}>
                    {perDiem.location_description ?? perDiem.location_code}
                    {perDiem.is_site_rate && ' · site rate'} ·{' '}
                    {perDiem.tiers
                      .map((t) => `${t.tier} $${Number(t.daily_amount).toFixed(2)}`)
                      .join(' · ')}
                  </p>
                  <label className="checkline">
                    <input
                      type="checkbox"
                      checked={perDiemCount > 0 && perDiemCount === perDiemEligible}
                      // Some but not all ticked — neither on nor off.
                      ref={(el) => {
                        if (el) el.indeterminate =
                          perDiemCount > 0 && perDiemCount < perDiemEligible
                      }}
                      onChange={(e) => togglePerDiemAll(e.target.checked)}
                    />
                    <span>
                      Per diem for the whole crew
                      {perDiemCount > 0 && (
                        <span className="muted">
                          {' '}— {perDiemCount} of {perDiemEligible}, $
                          {perDiemTotal.toFixed(2)} today
                        </span>
                      )}
                    </span>
                  </label>
                </>
              ) : (
                <p className="muted" style={{ marginTop: 0 }}>
                  {perDiem.reason ?? 'No per diem rate is set up for this job.'}
                </p>
              )}
            </div>
          )}

          {draft.crewKeys.map((key) => {
            const person = crewByKey.get(key)
            // Per diem is represented by the checkbox below, never as a row
            // here — showing both put the same fact on screen twice, editable
            // in two places that could disagree.
            const lines = draft.lines.filter(
              (l) =>
                l.sageEmployeeKey === key &&
                l.workTypeId !== perDiemType?.work_type_id,
            )
            const shift = draft.shifts[key]
            const hasOverride = Boolean(
              shift && (shift.start || shift.end || shift.note),
            )
            const open = openShifts.has(key) || hasOverride
            return (
              <div
                className={`card crew-card ${
                  ROLE_STYLE[person?.crew_role ?? '']?.card ?? ''
                }`}
                key={key}
              >
                <div className="crew-heading">
                  <div className="name-and-role">
                    <h2 className="title" style={{ margin: 0 }}>
                      {person?.display_name ?? key}
                    </h2>
                    {person?.crew_role && (
                      <span
                        className={`role-badge ${ROLE_STYLE[person.crew_role]?.cls ?? ''}`}
                      >
                        {person.crew_role}
                      </span>
                    )}
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{ minHeight: 36, fontSize: '0.8125rem' }}
                    onClick={() => removePerson(key)}
                  >
                    Remove from day
                  </button>
                </div>

                {/* Only where there is travel to code. Who drove is a fact
                    about THIS day — the same man drives to one job and rides
                    to the next — so it starts clear every time and is never
                    remembered the way the crew list is. It moves money: driver
                    pays a premium over rider. */}
                {travelType &&
                  lines.some((l) => l.workTypeId === travelType.work_type_id) && (
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                        fontSize: '0.875rem',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isDriver(key)}
                        onChange={() => toggleDriver(key)}
                      />
                      Drove on this job today
                      <span className="muted" style={{ fontSize: '0.75rem' }}>
                        {isDriver(key)
                          ? '· travel pays the driver rate'
                          : '· travel pays the rider rate'}
                      </span>
                    </label>
                  )}
                {lines.map((l) => {
                  const meta = ref.workTypes.find(
                    (w) => w.work_type_id === l.workTypeId,
                  )
                  return (
                    <div
                      key={l.id}
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'flex-end',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <div className="form-field" style={{ flex: 2, marginBottom: 0 }}>
                        <select
                          value={l.workTypeId}
                          onChange={(e) => changeWorkType(l.id, Number(e.target.value))}
                          aria-label="Type of work"
                        >
                          {/* Per diem is deliberately absent: the tick below
                              owns it. Offering it here too would let someone
                              create a per-diem row with a blank amount, which
                              the tick would then report as set. */}
                          {selectableTypes.map((w) => (
                            <option key={w.work_type_id} value={w.work_type_id}>
                              {fieldLabel(w)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Per diem and foreman pay carry a dollar amount and
                          post to Sage with zero hours. */}
                      <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                        <input
                          type="number"
                          inputMode="decimal"
                          step={l.entryType === 'Hours' ? '0.25' : '0.01'}
                          min="0"
                          max={l.entryType === 'Hours' ? '24' : undefined}
                          value={l.entryType === 'Hours' ? l.hours : l.amount}
                          onChange={(e) =>
                            setLine(
                              l.id,
                              l.entryType === 'Hours'
                                ? { hours: e.target.value }
                                : { amount: e.target.value },
                            )
                          }
                          placeholder={l.entryType === 'Hours' ? 'hrs' : '$'}
                          aria-label={
                            l.entryType === 'Hours' ? 'Hours' : 'Amount in dollars'
                          }
                        />
                      </div>

                      {/* Out-of-trade classification. Only on jobs that can
                          actually pay more than one, and only on hours — an
                          amount line is per diem or foreman pay, neither of
                          which is a trade. Hidden entirely on the ordinary
                          job, so the common case gains no control at all. */}
                      {canSplitTrade && l.entryType === 'Hours' && (
                        <div className="form-field" style={{ flex: 1.4, marginBottom: 0 }}>
                          <select
                            value={l.wageClassificationId ?? 0}
                            onChange={(e) =>
                              setLine(l.id, {
                                wageClassificationId: Number(e.target.value) || null,
                              })
                            }
                            aria-label={`Worked as, for ${person?.display_name ?? ''}`}
                          >
                            <option value={0}>Own trade</option>
                            {jobClasses.map((c) => (
                              <option key={c.wage_classification_id} value={c.wage_classification_id}>
                                as {c.description} · ${Number(c.reg_rate).toFixed(2)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* This person's travel went somewhere else. Only on
                          travel lines, and only once a destination is set for
                          the crew — the crew-wide picker above is the way in,
                          and offering a per-person one before that would put
                          the exception in front of the rule. */}
                      {isTravelType(l.workTypeId) &&
                        travelLines.some((x) => x.overrideJobNo) && (
                          <div className="form-field" style={{ flex: 1.6, marginBottom: 0 }}>
                            <select
                              value={l.overrideJobNo ?? ''}
                              onChange={(e) =>
                                setLine(l.id, { overrideJobNo: e.target.value || null })
                              }
                              aria-label={`Travelled to, for ${person?.display_name ?? ''}`}
                            >
                              <option value="">Stayed on this job</option>
                              {destinationGroups.assigned.length > 0 && (
                                <optgroup label="Your jobs">
                                  {destinationGroups.assigned.map((j) => (
                                    <option key={j.job_no} value={j.job_no}>
                                      to {j.job_no}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              <optgroup label="All open jobs">
                                {destinationGroups.rest.map((j) => (
                                  <option key={j.job_no} value={j.job_no}>
                                    to {j.job_no}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                          </div>
                        )}

                      {/* Any line can go, including Regular: someone who only
                          rode out has travel hours and no regular ones. */}
                      <button
                        className="btn btn-secondary"
                        onClick={() => removeLine(l.id)}
                        aria-label={`Remove ${meta?.label ?? 'line'} for ${person?.display_name ?? ''}`}
                        style={{ minWidth: 48 }}
                        disabled={lines.length === 1 && draft.crewKeys.length === 1}
                      >
                        ✕
                      </button>
                    </div>
                  )
                })}
                {perDiemType && perDiem?.has_rate && perDiemFor(key) !== null && (
                  <label className="checkline" style={{ marginBottom: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(perDiemLine(key))}
                      onChange={() => togglePerDiem(key)}
                    />
                    <span>
                      Per diem{' '}
                      <span className="mono">
                        ${Number(perDiemFor(key)).toFixed(2)}
                      </span>
                    </span>
                  </label>
                )}

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary" onClick={() => addLine(key)}>
                    + Add travel or other
                  </button>
                  {/* One tap for the case this exists for: part of the day was
                      worked in another trade. Adds a second line at that
                      classification with the hours left blank — the split is
                      somebody's pay, so it gets typed, not guessed. */}
                  {canSplitTrade &&
                    (() => {
                      const base = lines.find(
                        (x) => x.entryType === 'Hours' && x.wageClassificationId === null,
                      )
                      const other = jobClasses.find((c) => !c.is_employee_default)
                      if (!base || !other) return null
                      const already = lines.some(
                        (x) => x.wageClassificationId === other.wage_classification_id,
                      )
                      if (already) return null
                      return (
                        <button
                          className="btn btn-secondary"
                          onClick={() => splitForTrade(base, other.wage_classification_id)}
                        >
                          + Split for {other.description.toLowerCase()}
                        </button>
                      )
                    })()}
                </div>

                {/* Collapsed by default. Most people match the crew — on the
                    sheet this was drawn from, five of seven task cells were
                    blank. Opening it is the exception, so it costs a tap
                    rather than occupying the screen for everybody. */}
                {open ? (
                  <div
                    style={{
                      marginTop: '0.75rem',
                      paddingTop: '0.75rem',
                      borderTop: '1px solid var(--border, #e2e8f0)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                      <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                        <label htmlFor={`start-${key}`}>Start</label>
                        <input
                          id={`start-${key}`}
                          type="time"
                          value={shift?.start ?? ''}
                          onChange={(e) => setShift(key, { start: e.target.value })}
                          placeholder={draft.crewStart}
                        />
                      </div>
                      <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                        <label htmlFor={`end-${key}`}>End</label>
                        <input
                          id={`end-${key}`}
                          type="time"
                          value={shift?.end ?? ''}
                          onChange={(e) => setShift(key, { end: e.target.value })}
                          placeholder={draft.crewEnd}
                        />
                      </div>
                      <div style={{ flex: 1, paddingBottom: '0.6rem' }}>
                        <span className="muted" style={{ fontSize: '0.8125rem' }}>
                          {spanLabel(
                            shift?.start || draft.crewStart,
                            shift?.end || draft.crewEnd,
                          ) ?? '—'}
                        </span>
                      </div>
                    </div>
                    <div
                      className="form-field"
                      style={{ marginTop: '0.75rem', marginBottom: 0 }}
                    >
                      <input
                        value={shift?.note ?? ''}
                        onChange={(e) => setShift(key, { note: e.target.value })}
                        placeholder={draft.workPerformed || 'What they did today'}
                        maxLength={500}
                        aria-label={`Work performed by ${person?.display_name ?? key}`}
                      />
                    </div>
                    {hasOverride && (
                      <button
                        className="btn btn-secondary"
                        style={{ marginTop: '0.5rem', minHeight: 36, fontSize: '0.8125rem' }}
                        onClick={() => clearShift(key)}
                      >
                        Same as crew
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: '0.5rem', minHeight: 36, fontSize: '0.8125rem' }}
                    onClick={() => openShift(key)}
                  >
                    Different shift or task
                  </button>
                )}
              </div>
            )
          })}

          <div className="card">
            <h2>Note (optional)</h2>
            <input
              value={draft.note}
              onChange={(e) => update({ note: e.target.value })}
              placeholder="Anything the office should know"
              maxLength={500}
            />
          </div>

          <div className="card">
            <p>
              <strong>{draft.crewKeys.length}</strong> crew ·{' '}
              <strong>{totals.hours.toFixed(2)}</strong> hours
              {totals.amount > 0 && (
                <>
                  {' '}
                  · <strong>${totals.amount.toFixed(2)}</strong>
                </>
              )}
            </p>
            <p className="muted" style={{ fontSize: '0.8125rem' }}>
              Submitting records and approves this crew day.
            </p>
            {!canPickAnyone && !(draft.checkIn && draft.checkIn.jobNo === draft.jobNo) && (
              <div className="form-field">
                <label htmlFor="no-loc-reason">
                  No location recorded — why? (required)
                </label>
                <input
                  id="no-loc-reason"
                  value={draft.noCheckInReason ?? ''}
                  onChange={(e) => update({ noCheckInReason: e.target.value })}
                  placeholder="e.g. at the hotel, no signal on site"
                  maxLength={200}
                />
              </div>
            )}
            <button className="btn btn-block" onClick={() => void submit()} disabled={submitting}>
              {submitting ? 'Sending…' : 'Submit crew day'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
